import {
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { AgxButton, AgxCollapsible, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import { useState } from 'react';
import SpecialConditionClause from './components/SpecialConditionClause';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import { findSpecialConditionDueDate } from '../specialConditionDueDate';

interface SpecialConditionCollapsiblePanelProps {
  contractId?: string;
  renderedSpecialCondition: RenderedSpecialCondition;
  specialCondition: SpecialConditionData | undefined;
  onDeleteCondition: (conditionId: string) => void;
  skipDeleteConfirmation?: boolean;
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => void | Promise<void>;
}

const SpecialConditionCollapsiblePanel = ({
  renderedSpecialCondition,
  specialCondition,
  onDeleteCondition,
  skipDeleteConfirmation = false,
  contractId,
  onDueDateUpdated,
}: SpecialConditionCollapsiblePanelProps) => {
  const title = `${renderedSpecialCondition.position}. ${renderedSpecialCondition.title}`;

  const dateItems = (() => {
    const directItem = renderedSpecialCondition.directDueDate
      ? [
          {
            id: renderedSpecialCondition.id ?? '',
            label: `${renderedSpecialCondition.position}: ${renderedSpecialCondition.title}`,
            dueDate: renderedSpecialCondition.directDueDate,
          },
        ]
      : [];

    return [
      ...directItem,
      ...(renderedSpecialCondition.clauses ?? []).map((clause) => ({
        id: clause.clauseId ?? '',
        label: `Clause ${clause.clausePosition}: ${clause.clauseText}`,
        dueDate: clause.dueDate,
      })),
    ];
  })();

  const onConditionDeleted = () => {
    if (!renderedSpecialCondition?.id) return;

    onDeleteCondition(renderedSpecialCondition.id);
  };

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  return (
    <AgxCollapsible
      key={renderedSpecialCondition.id}
      enableToggle
      open
      customTitle={
        <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
          <AgxTypography
            id={`special-condition-${renderedSpecialCondition.id}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {title}
          </AgxTypography>
        </AgxSpacer>
      }
      variant={'withDivider'}
      content={
        <AgxSpacer direction="vertical" gap={'md'}>
          {dateItems.map((item, index) => (
            <SpecialConditionClause
              itemId={item.id}
              label={item.label}
              dueDate={item.dueDate}
              key={item.id}
              isLastItem={index === dateItems.length - 1}
              conditionId={renderedSpecialCondition.id ?? ''}
              contractId={contractId}
              dueDateCondition={findSpecialConditionDueDate(specialCondition, item.id)}
              onDueDateUpdated={onDueDateUpdated}
            />
          ))}

          <AgxSpacer direction="horizontal" fill contentJustification="end">
            <AgxButton
              id={`delete-${renderedSpecialCondition.id}`}
              variant="link"
              danger
              text="Delete"
              onClick={() =>
                skipDeleteConfirmation ? onConditionDeleted() : setShowDeleteConfirmation(true)
              }
            />
            {!skipDeleteConfirmation && (
              <DeleteConfirmation
                name={`${renderedSpecialCondition.title} and all its clauses`}
                title="Really delete special condition?"
                lineOne="Are you sure you want to delete special condition"
                lineTwo="This cannot be undone."
                primaryActionTitle="Permanently delete special condition"
                secondaryActionTitle="No, cancel"
                showPopUp={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={onConditionDeleted}
              />
            )}
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  );
};

export default SpecialConditionCollapsiblePanel;
